import { LENS_LABELS } from '../../lib/lens-labels';
import { colors } from '../../lib/theme';

function Step({ n, title, desc }: { n: string; title: string; desc: string }) {
  return (
    <li
      className="rounded-[14px] p-[18px_16px]"
      style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
    >
      <span className="font-mono text-xs font-semibold" style={{ color: colors.blue }}>
        {n}
      </span>
      <div className="mt-3 text-[15px] font-semibold">{title}</div>
      <div className="mt-1.5 text-[13px] leading-relaxed" style={{ color: colors.textMuted }}>
        {desc}
      </div>
    </li>
  );
}

export function HowItWorks() {
  return (
    <section id="how" className="mt-16">
      <h2 className="text-[22px] font-bold tracking-tight">How it works</h2>
      <ol className="mt-[18px] grid gap-[13px] sm:grid-cols-2 lg:grid-cols-4">
        <Step n="01" title="Paste tickers" desc="Drop in symbols (AAPL, VOO…) or upload a CSV of your holdings." />
        <Step
          n="02"
          title="Choose lenses"
          desc="Occupation, defense, Shariah — toggle on/off or pick a preset."
        />
        <Step n="03" title="Run check" desc="We match companies to curated datasets and compute a verdict." />
        <Step n="04" title="See sources" desc="Every result links to public research or filings. No black box." />
      </ol>
    </section>
  );
}

function SourceCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div
      className="rounded-[14px] p-[18px_16px]"
      style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
    >
      <div className="text-sm font-semibold">{title}</div>
      <ul className="mt-2.5 space-y-1.5">
        {items.map((i) => (
          <li key={i} className="flex gap-1.5 font-mono text-[11.5px]" style={{ color: colors.textMuted }}>
            <span style={{ color: colors.blue }}>·</span>
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DataSources() {
  return (
    <section id="sources" className="mt-14">
      <h2 className="text-[22px] font-bold tracking-tight">Data sources</h2>
      <div className="mt-[18px] grid gap-[13px] md:grid-cols-2 lg:grid-cols-4">
        <SourceCard
          title={LENS_LABELS.occupationSources}
          items={['AFSC Investigate', 'Who Profits', 'OHCHR A/HRC/60/19 (UN HRC)']}
        />
        <SourceCard
          title="Defense contractors"
          items={['SIPRI Arms Industry Database (primary)', 'USAspending DoD contracts (when loaded)']}
        />
        <SourceCard
          title="Shariah compliance"
          items={[
            'DJIM/S&P-style ratio rules (33/33/49, in-app)',
            'Interest income / revenue ≤ 5% (FMP + Yahoo fallback)',
            'Financial Modeling Prep (financials ingest)',
            'Curated haram business screen',
            'Yahoo Finance fallback (flagged estimated)',
          ]}
        />
        <SourceCard
          title="ETF holdings"
          items={[
            '6 launch ETFs ingested (VOO, SPY, QQQ, HLAL, SPUS, UMMA)',
            'Lookthrough + dollar X-Ray when CSV includes position values',
          ]}
        />
      </div>
      <p className="mt-4 text-[13px]" style={{ color: colors.textMuted }}>
        All sources are publicly available and transparently cited. Coverage expands with user feedback.
      </p>
    </section>
  );
}
